// src/components/finance/FinanceCashflow.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchFinanceCashflow } from '../../api.js'
import FinanceNav from './FinanceNav.jsx'

function isoDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}
function fmtDateFR(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString('fr-FR')
}
function fmtMoney(v) {
  const n = Number(v || 0)
  return `${n.toLocaleString('fr-FR')} XOF`
}

export default function FinanceCashflow() {
  const defaultRange = useMemo(() => {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return { from: isoDate(from), to: isoDate(to) }
  }, [])

  const [from, setFrom] = useState(defaultRange.from)
  const [to, setTo] = useState(defaultRange.to)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const data = await fetchFinanceCashflow({ from, to })
      setRows(data?.series || [])
    } catch (e) {
      setErr(String(e?.message || e || 'Erreur chargement trésorerie'))
    } finally {
      setLoading(false)
    }
  }, [from, to])

  useEffect(() => { load() }, [load])

  return (
    <div className="p-6 space-y-6 min-h-screen">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Trésorerie</h1>
        <div className="text-sm text-slate-500 mt-1">
          Période: <span className="font-semibold">{fmtDateFR(from)}</span> → <span className="font-semibold">{fmtDateFR(to)}</span>
        </div>
      </div>

      <FinanceNav />

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Du</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Au</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2" />
          </div>
          <div>
            <button type="button" className="px-4 py-2 rounded-lg bg-slate-900 text-white w-full" onClick={load}>🔄</button>
          </div>
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 text-xs text-slate-500 font-semibold">
          <div className="col-span-3">Date</div>
          <div className="col-span-3 text-right">Entrées</div>
          <div className="col-span-3 text-right">Sorties</div>
          <div className="col-span-3 text-right">Net</div>
        </div>
        {loading ? (
          <div className="p-4 text-sm text-slate-500">Chargement…</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <div key={`${r.day}-${i}`} className="grid grid-cols-12 gap-3 px-4 py-3 hover:bg-slate-50">
                <div className="col-span-3 text-sm">{fmtDateFR(r.day)}</div>
                <div className="col-span-3 text-right text-sm">{fmtMoney(r.inflow)}</div>
                <div className="col-span-3 text-right text-sm">{fmtMoney(r.outflow)}</div>
                <div className="col-span-3 text-right text-sm font-semibold">{fmtMoney(r.net)}</div>
              </div>
            ))}
            {!rows.length && (
              <div className="p-4 text-sm text-slate-500">Aucune donnée.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
